import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isConnectionOwned } from '@/components/OwnedChip'
import { useViewOnly } from '@/components/templates/app-shell'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ApiError, patchJSON, postJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import { EnrolmentFields, initialEnrolmentValues, type EnrolmentValues } from './EnrolmentFields'
import { EnrolmentPersonPicker } from './EnrolmentPersonPicker'
import { EnrolmentCoursePicker } from './EnrolmentCoursePicker'
import type { Enrolment } from './enrolment'

interface Props {
  courseId?: number
  courseTitle?: string
  person?: { id: number; name: string }
  unit: string
  enrolment?: Enrolment
  onClose: () => void
  onExisting?: (id: number) => void
}

export function EnrolmentDialog(props: Props) {
  const viewOnly = useViewOnly()
  if (viewOnly || isConnectionOwned(props.enrolment?.owner)) return null
  return <EnrolmentForm {...props} />
}

function EnrolmentForm({
  courseId: fixedCourseId,
  courseTitle,
  person,
  unit,
  enrolment,
  onClose,
  onExisting,
}: Props) {
  const [values, setValues] = useState(() => initialEnrolmentValues(enrolment))
  const [personId, setPersonId] = useState<number | null>(person?.id ?? null)
  const [courseId, setCourseId] = useState<number | null>(fixedCourseId ?? null)
  const client = useQueryClient()
  const save = useMutation({
    mutationFn: () =>
      enrolment
        ? patchJSON<{ enrolment: Enrolment }>(`/api/v1/enrolments/${enrolment.id}`, {
            enrolment: enrolmentPayload(values),
          })
        : postJSON<{ enrolment: Enrolment }>('/api/v1/enrolments', {
            enrolment: {
              person_id: personId,
              course_id: courseId,
              enrolled_on: values.enrolled_on,
            },
          }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['enrolments'] })
      void client.invalidateQueries({ queryKey: ['course', courseId] })
      void client.invalidateQueries({ queryKey: ['courses'] })
      onClose()
    },
  })
  const disabled = save.isPending || !readyToSave(enrolment, personId, courseId, values.enrolled_on)
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !save.isPending) onClose()
      }}
    >
      <DialogContent>
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            if (!disabled) save.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>{enrolment ? 'Edit enrolment' : 'Add enrolment'}</DialogTitle>
            <DialogDescription>{courseTitle ?? person?.name}</DialogDescription>
          </DialogHeader>
          <fieldset disabled={save.isPending} className="space-y-4">
            {save.error && (
              <EnrolmentError error={save.error} courseId={courseId} onExisting={onExisting} />
            )}
            {enrolment ? (
              <p className="text-body-sm font-medium">{enrolment.person_name}</p>
            ) : person ? (
              <EnrolmentCoursePicker
                value={courseId}
                onChange={setCourseId}
                error={[
                  ...apiFieldMessages(save.error, 'course'),
                  ...apiFieldMessages(save.error, 'course_id'),
                ].join(' ')}
              />
            ) : (
              <EnrolmentPersonPicker
                value={personId}
                onChange={setPersonId}
                error={[
                  ...apiFieldMessages(save.error, 'person'),
                  ...apiFieldMessages(save.error, 'person_id'),
                ].join(' ')}
              />
            )}
            <EnrolmentFields
              editing={Boolean(enrolment)}
              unit={unit}
              values={values}
              update={(patch) => setValues((current) => ({ ...current, ...patch }))}
              error={save.error}
            />
          </fieldset>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={save.isPending} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={disabled}>
              {save.isPending ? 'Saving…' : enrolment ? 'Save changes' : 'Add enrolment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function enrolmentPayload(values: EnrolmentValues) {
  const { credits_earned, ...editable } = values
  return {
    ...editable,
    completed_on: values.completed_on || null,
    withdrawn_on: values.withdrawn_on || null,
    score: values.score || null,
    ...(values.status === 'completed' ? { credits_earned: credits_earned || null } : {}),
  }
}

function readyToSave(
  enrolment: Enrolment | undefined,
  personId: number | null,
  courseId: number | null,
  date: string,
) {
  return Boolean(enrolment || (personId && courseId && date))
}

function EnrolmentError({
  error,
  courseId,
  onExisting,
}: {
  error: Error
  courseId: number | null
  onExisting?: (id: number) => void
}) {
  const duplicate =
    error instanceof ApiError && error.status === 409
      ? (error.body as { enrolment_id?: number })
      : null
  const general = [...apiFieldMessages(error, 'base'), ...apiFieldMessages(error, 'course')]
  return (
    <Alert>
      {general.length ? general.join(' ') : error.message}
      {duplicate?.enrolment_id && (
        <a
          className="ml-2 text-primary underline"
          href={`/courses/${courseId}#enrolment-${duplicate.enrolment_id}`}
          onClick={(event) => {
            if (onExisting) {
              event.preventDefault()
              onExisting(duplicate.enrolment_id!)
            }
          }}
        >
          View existing enrolment
        </a>
      )}
    </Alert>
  )
}
